// src/utils/groupes.ts — SCE Issues
// Multi-équipements (Lot 14, option C retenue par les coordinateurs le 26/09).
// Pur, testable.
//
// ─── Le modèle ───
//
// Un même défaut sur trois machines = TROIS fiches, une par machine, reliées
// par la colonne Groupe : l'ID de la première fiche (« P2026-0042 »), qu'elle
// porte aussi. Chaque fiche garde son équipement, son statut, ses
// statistiques : filtres, agrégats, EDE et exports n'ont rien à savoir des
// groupes. Un défaut sur 3 machines compte donc pour 3 (réponse des
// coordinateurs) ; le « 1 » reste calculable en comptant les groupes.
//
// ─── Les réponses des coordinateurs (note du 26/09) ───
//
//   Plusieurs projets (PN) dans un groupe   → oui
//   Qualifier / clôturer tout le groupe     → oui, décochable
//   Ajouter une machine après coup          → oui, par le coordinateur
//   Commentaire                             → sur la fiche, avec une coche pour
//                                             le publier sur tout le groupe

import {
  A_QUALIFIER,
  cleRapprochement,
  estActif,
  type Issue,
} from '@/types/issue.types'

/** Les AUTRES fiches du groupe de `issue`, par ID croissant ; vide pour une fiche seule. */
export function membresDuGroupe(issue: Issue, tous: readonly Issue[]): Issue[] {
  const groupe = cleRapprochement(issue.groupe)
  if (!groupe) return []
  return tous
    .filter((i) => i.id !== issue.id && cleRapprochement(i.groupe) === groupe)
    .sort((a, b) => a.id.localeCompare(b.id))
}

function aQualifier(i: Issue): boolean {
  return i.famille === A_QUALIFIER || i.criticite === A_QUALIFIER
}

/**
 * Fiches sœurs à qualifier (ou à annuler) avec celle-ci : actives et encore à
 * qualifier. Une sœur déjà qualifiée à part garde sa qualification.
 */
export function eligibleQualification(i: Issue): boolean {
  return estActif(i) && aQualifier(i)
}

/** Fiches sœurs à clore avec celle-ci, ou à commenter : les actives. */
export function eligibleCloture(i: Issue): boolean {
  return estActif(i)
}

// ─── Clore : ce problème seul ou tout le groupe (02/11) ───
//
// La case « Clôturer aussi », cochée d'office, a clos un groupe de 8 alors
// que Thibaut pensait ne clore qu'un équipement. Depuis, on DEMANDE : aucun
// choix par défaut, le bouton attend la réponse. Sans sœur active, rien à
// demander, et rien n'est jamais appliqué au groupe.

/** Réponse à « ce problème seul ou tout le groupe ? » — null : pas encore répondu. */
export type PorteeGroupe = 'seul' | 'groupe'

/** Il reste à choisir avant de clore. */
export function porteeAChoisir(nbSoeurs: number, portee: PorteeGroupe | null): boolean {
  return nbSoeurs > 0 && portee === null
}

/** La clôture part aussi sur les sœurs : seulement si on l'a choisi, et qu'il y en a. */
export function porteeSurLeGroupe(nbSoeurs: number, portee: PorteeGroupe | null): boolean {
  return nbSoeurs > 0 && portee === 'groupe'
}

/** « 3 machines » */
export function libelleMachines(n: number): string {
  return `${n} équipement${n > 1 ? 's' : ''}`
}

/**
 * Le commentaire publié aussi sur une fiche sœur porte d'où il vient :
 * « …\n\n(publié depuis P2026-0119) ». Seule règle d'écriture de ce renvoi.
 */
export function texteRepublie(texte: string, depuis: string): string {
  return `${texte}\n\n(publié depuis ${depuis})`
}

const RENVOI_GROUPE = /\s*\(publié depuis [^)]+\)\s*$/

/**
 * Le texte sans son renvoi « (publié depuis …) » (04/11) : le commentaire et
 * ses copies sur les fiches sœurs se reconnaissent identiques, et tiennent sur
 * une ligne dans l'activité récente.
 */
export function sansRenvoiGroupe(texte: string): string {
  return texte.replace(RENVOI_GROUPE, '')
}

/**
 * Une ligne par groupe : la première fiche rencontrée représente le groupe,
 * les suivantes disparaissent de la liste. Sert aux files de l'accueil
 * (« À qualifier montre le groupe sur une ligne »).
 *
 * @returns la liste réduite (ordre conservé) et, pour chaque représentant
 *          d'un groupe de 2 fiches ou plus, le nombre de machines de la liste.
 */
export function unParGroupe(issues: readonly Issue[]): { issues: Issue[]; machines: Map<string, number> } {
  const representant = new Map<string, Issue>()
  const machines = new Map<string, number>()
  const liste: Issue[] = []
  for (const i of issues) {
    const groupe = cleRapprochement(i.groupe)
    if (!groupe) {
      liste.push(i)
      continue
    }
    const rep = representant.get(groupe)
    if (rep) {
      machines.set(rep.id, (machines.get(rep.id) ?? 1) + 1)
      continue
    }
    representant.set(groupe, i)
    liste.push(i)
  }
  return { issues: liste, machines }
}

/** Ce que l'appli fait après une action appliquée à tout le groupe. */
export interface BilanGroupe {
  /** Fiches sœurs traitées. */
  faits: string[]
  /** Fiches sœurs laissées de côté : déjà qualifiées, closes, annulées… */
  ignores: string[]
  /** Fiches sœurs en échec : l'action est à refaire depuis leur fiche. */
  echecs: string[]
}

export const BILAN_VIDE: BilanGroupe = { faits: [], ignores: [], echecs: [] }

/** Suite du message de la fiche : « — aussi sur P2026-0043 et P2026-0044 ». */
export function resumerBilan(bilan: BilanGroupe): string {
  if (bilan.faits.length === 0) return ''
  const liste = bilan.faits.length <= 2
    ? bilan.faits.join(' et ')
    : `${bilan.faits.length} autres équipements`
  return ` — aussi sur ${liste}`
}
